import type { SlotPlacementId } from '@hen-site/types/monarch';
import { useGlobalProps } from '@hen-site/component/GlobalContextProvider';
import {
  CohesionErrorNormalized,
  MonarchExperience,
} from '@redventures/cohesion-utils';
import type {
  MonarchEvaluationsRuleSet,
  MonarchEvaluationsTrafficFlow,
  MonarchComponentVariant,
} from '@redventures/cohesion-utils';
import { useMemo } from 'react';

export type SlotPlacement<T> = {
  value: T | null;
  slotName?: string;
  slotCustomId?: SlotPlacementId;
};

type UseMonarchSlotDataReturn<T> = {
  data: SlotPlacement<T>;
  isLoading: boolean;
  error: CohesionErrorNormalized | null;
  returnType?: string;
  experiences?: MonarchExperience;
};

type TrafficFlow = MonarchEvaluationsTrafficFlow & {
  returnDetails?: MonarchExperience;
};

export function useMonarchSlotPlacement<T>(
  slotCustomId: SlotPlacementId | undefined | '',
): UseMonarchSlotDataReturn<T> {
  const { monarchTrafficFlow } = useGlobalProps();
  const { result, isLoading, err, slots } = monarchTrafficFlow;

  return useMemo(() => {
    if (!slotCustomId) {
      return { data: { value: null }, isLoading: false, error: null };
    }
    if (isLoading) {
      return { data: { value: null }, isLoading: true, error: null };
    }
    if (err) {
      return { data: { value: null }, isLoading: false, error: err };
    }

    const slotData: T | null =
      (slots?.[slotCustomId]?.value?.data as T) ?? null;
    const slotName: string = slots?.[slotCustomId]?.slot?.slotName ?? '';
    const source: MonarchEvaluationsRuleSet = result?.source ?? {};
    const trafficFlow = source?.trafficFlow as TrafficFlow | undefined;
    const returnType = trafficFlow?.returnType;
    const experiences = trafficFlow?.returnDetails;

    const dynamicExperienceJoQdf = source?.dynamicExperienceJoQdf;
    const dynamicExperienceValue =
      dynamicExperienceJoQdf?.value as MonarchComponentVariant;

    if (dynamicExperienceValue && slotCustomId === 'aboveTheFold') {
      return {
        data: {
          value: {
            flowId: 'personalized-dcs',
            ...((dynamicExperienceValue?.data ?? dynamicExperienceValue) as T),
          } as T,
          slotName,
          slotCustomId,
        },
        isLoading: false,
        error: null,
        returnType,
        experiences,
      };
    }

    return {
      data: { value: slotData, slotName, slotCustomId },
      isLoading: false,
      error: null,
      returnType,
      experiences,
    };
  }, [slotCustomId, isLoading, err, slots, result]);
}
